import { useState, useEffect, useMemo, useCallback } from 'react';
import { Calculator, Save, Pencil } from 'lucide-react';
import { Card, Panel, Divider, HStack } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon, Tooltip } from '@components/base/display';
import { Button } from '@components/base/actions';
import { TextField } from '@components/base/inputs';
import { Alert, Spinner, useToast } from '@components/base/feedback';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  SelectFieldOptions,
  SelectableOption,
} from '@components/integrated/entity-form';
import type { ColumnConfig } from '@components/integrated/data-list';
import {
  applyLayoutOverrides,
  applyColumnOverrides,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { claimflowApiClient } from '../api/claimflowApiClient';
import { commentsService } from '../api/commentsService';
import {
  estimationControlService,
  requestErrorDetail,
} from '../api/estimationControlService';
import type { VeosGuaranteeToClose } from '../api/taskService';
import { useClaimFlowAuthStore } from '../auth/store';
import type { EstimationRecoursRow } from '../types';
import {
  DEFAULT_DEDUCTION_POSTES,
  isBelowMinimum,
  savedPosteAmount,
  signedAmountString,
} from './deductionPostes';
import {
  COMMENT_GROUP_FIELDS,
  ESTIMATION_COMMENT_TEXT,
  FIELD_COMMENTAIRE,
  buildCommentaireFieldConfig,
  commentValue,
  requiredMessageOf,
  type EstimationSubmitError,
} from './estimationCommentaire';

// ===========================================================================
// « Estimation recours » (mahhp 22442) — sibling of `EstimationCard`
// ---------------------------------------------------------------------------
// Same shape as the "Nouvelle provision" card, provisioning the RECOURS
// (recoveries) we expect to obtain instead of the charges. Deliberate
// differences from the blueprint, all spec-mandated:
//
//   * NO perte-totale checkbox and NO VRADE panel ⇒ no `sinistre-inline-get`
//     GET and no `sinistre-update` PUT. The data flow is exactly one GET on
//     mount/refresh + a POST loop on submit.
//   * The poste rows come from `boby-ws2-gs-tab-estimations-recours-all`.
//   * Submit iterates over the MODIFIED rows only (`value !== baseline`) —
//     « Idéalement on itère seulement sur les montants modifiés ». The
//     blueprint instead posts every filled row. Consequently the submit gate is
//     "at least one modified row" rather than "at least one filled row".
//   * The write endpoint is the dedicated `sinistre-prevision` slug
//     (`POST /sinistre/{id_sin}/prevision`): the ERP proxy endpoint that
//     persists each modified recours estimation (spec mahhp 22442).
//
// Configurable-ready contract (rule-binding surface — DO NOT rename keys):
// Stable field keys (EntityForm config / `_layoutOverrides.fields`):
//   `garantie`     — the Garantie select
//   `commentaire`  — the required Commentaire textarea (mahhp 22858), rendered
//                    in its own field group under the « Recours estimé »
//                    total; also the `commentaire` key of the
//                    WS2_GS_VER_ESTIMATION pre-check
// Stable column keys (poste-list headers / `_layoutOverrides.columns`):
//   `poste`     — the poste label column
//   `montant`   — the editable amount column
// Labels are display-only and overridable; the keys above are the contract a
// PM/admin rule targets. The card persists through a bespoke POST loop, so the
// EntityForm config is driven in CONTROLLED mode and submitted by this card's
// own `handleSubmit` — the config `endpoint` is a formal placeholder and
// EntityForm's built-in submit is never used. The poste-list BODY (controlled
// inputs, MINIMUM validation, the live recours total) stays imperative; only
// its HEADERS are declarative.
//
// Submit chain (mahhp 22858): (a) the Commentaire must be filled (unless an
// admin hid the field) and the session must carry a `CD_UTIL`; (b) the VEOS
// pre-check `boby-ws2-gs-ver-estimation` must answer `VERIF = OK` — anything
// else blocks before any write; (c) the prevision POST loop below, unchanged;
// (d) the comment is posted on the claim (`claimflow-post-comment`).
// ===========================================================================

// ---------------------------------------------------------------------------
// ERP proxy envelope types — mirror EstimationCard / SettlementCard
// ---------------------------------------------------------------------------

interface ErpProxyResponse<T> {
  success: boolean;
  http_status?: number;
  data: T;
  error?: { code: string; message: string } | null;
}

type ErpListResponse<T> = ErpProxyResponse<{ total: number; data: T[] }>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EstimationRecoursCardProps {
  /**
   * Veos session id. Part of the card's registered prop contract (every
   * claimflow card carries it, and rules may target it). The recours data
   * flow itself is session-less — the estimations GET and the prevision POSTs
   * are keyed by `id_sin` alone — so it is only forwarded as `ses` to the
   * `claimflow-post-comment` workflow with the Commentaire (mahhp 22858).
   */
  ses: string;
  idSin: string;
  /**
   * Claim reference (`NCBT_SIN`), forwarded as `ncbt_sin` to the
   * `claimflow-post-comment` workflow with the Commentaire (mahhp 22858) —
   * mirrors `CommentsPanel.ncbtSin`. The workflow only reads it for @mention
   * tasks, which this card never creates (`mentions: []`), hence optional.
   */
  ncbtSin?: string;
  /**
   * CD_PSD codes whose amount is subtracted from the recours total (Franchise,
   * Valeur de sauvetage). Their amount is forced NEGATIVE end to end — input,
   * display, save (mahhp 22851, same as `EstimationCard`). Default
   * {@link DEFAULT_DEDUCTION_POSTES} (`FRAN`, `SAUV`, `ASSFRC`, `ERVSRC`);
   * tenant-overridable via `setProp`.
   */
  deductionPostes?: string[];
  /**
   * Source of the Garantie select options (full replace). Passed from
   * `TaskDetailPage`'s `guaranteesToClose` state; mirrors
   * `EstimationCard.guaranteesToClose`. Default []. The poste rows still come
   * from the recours estimations fetch (filtered by the selected CD_GAR) — the
   * card never fetches the garanties itself.
   */
  guaranteesToClose?: VeosGuaranteeToClose[];
  onEstimationSaved?: () => void;
  /**
   * Incoming refresh trigger: when the parent bumps this value the estimations
   * fetch re-runs (e.g. after `OpenCoverageCard` opens new guarantees). This is
   * a parent-driven runtime control signal, NOT a tenant-configurable value, so
   * it is intentionally absent from the rule registry schema.
   */
  refreshKey?: number;
  /**
   * Per-tenant field/column layout overrides injected by the
   * `withComponentRules` HOC. Underscored to match the injected meta-prop
   * convention; `undefined` at baseline ⇒ the form config + poste columns are
   * returned unchanged (byte-identical render). `fields` merge into the
   * EntityForm config (`garantie`/`commentaire`); `columns` merge into the
   * poste column descriptor (`poste`/`montant`).
   */
  _layoutOverrides?: LayoutOverrides;
}

/** Per-row form state derived from an `EstimationRecoursRow`. */
interface PosteRow {
  row: EstimationRecoursRow;
  /**
   * Current input value (string). Typed amounts on a deduction poste are
   * already normalised to `-|n|`; a legacy positive stored amount is kept raw
   * here and signed at read time (display / modified test / total / save).
   */
  value: string;
  /**
   * Pre-filled value at load / after a successful save. "modified" = the
   * SIGNED value differs from the SIGNED baseline (so a legacy positive
   * deduction shown negative on load is neither flagged nor posted).
   */
  baseline: string;
  /** Validation error (MINIMUM floor) or undefined. */
  error?: string;
}

// ---------------------------------------------------------------------------
// Helpers — EUR_FORMATTER + extractErpErrorMessage mirror EstimationCard
// ---------------------------------------------------------------------------

const EUR_FORMATTER = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

function extractErpErrorMessage(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const response = (err as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (typeof data !== 'object' || data === null) return undefined;
  const envelope = data as {
    error?: { message?: unknown };
    raw_response?: { message?: unknown };
  };
  if (typeof envelope.error?.message === 'string' && envelope.error.message.length > 0) {
    return envelope.error.message;
  }
  if (
    typeof envelope.raw_response?.message === 'string' &&
    envelope.raw_response.message.length > 0
  ) {
    return envelope.raw_response.message;
  }
  return undefined;
}

/** Treat null / undefined / '' as "no value present". */
function hasValue(v: string | number | null | undefined): boolean {
  return v !== null && v !== undefined && String(v) !== '';
}

/**
 * Pre-fill precedence: MNT_SGD if present, else VALEUR_DEFAUT when proposed,
 * else ''. Live preprod rows routinely carry `MNT_SGD: null` with
 * `DEFAUT_PROPOSE: 1` and `VALEUR_DEFAUT: null` — that combination falls
 * through to '' (an empty input), never "null".
 */
function prefillValue(row: EstimationRecoursRow): string {
  if (hasValue(row.MNT_SGD)) return String(row.MNT_SGD);
  if (Number(row.DEFAUT_PROPOSE) === 1 && hasValue(row.VALEUR_DEFAUT)) {
    return String(row.VALEUR_DEFAUT);
  }
  return '';
}

function rowKey(row: EstimationRecoursRow): string {
  return `${row.CD_GAR}-${row.CD_PSD}`;
}

// ---------------------------------------------------------------------------
// Declarative config — fields (EntityForm) + poste-list column descriptor
// ---------------------------------------------------------------------------

const FIELD_GARANTIE = 'garantie';

const COL_POSTE = 'poste';
const COL_MONTANT = 'montant';

const CARD_TITLE = 'Estimation recours';
const SUBMIT_LABEL = "Enregistrer l'estimation";

/**
 * Build the EntityForm config for the card's form controls: the Garantie
 * select (top group) and the Commentaire textarea (bottom group,
 * `COMMENT_GROUP_FIELDS`). The Garantie select's options are runtime-derived
 * from the `guaranteesToClose` prop and injected here (not a config
 * `optionsEndpoint`). `endpoint` is a formal placeholder — submit is bespoke
 * (see file header).
 */
function buildEstimationRecoursFormConfig(garantieItems: SelectableOption[]): EntityFormConfig {
  return {
    id: 'estimation-recours-card',
    endpoint: {
      // Formal placeholder — EntityForm's built-in submit is never used; the
      // card persists through its own POST loop in `handleSubmit`.
      erpEndpoint: 'noop',
      method: 'POST',
    },
    fields: {
      [FIELD_GARANTIE]: {
        type: 'select',
        label: 'Garantie',
        order: 10,
        options: {
          items: garantieItems,
          placeholder: 'Sélectionner une garantie',
        } as SelectFieldOptions,
      },
      [FIELD_COMMENTAIRE]: buildCommentaireFieldConfig(20),
    },
    actions: {
      // The card renders its own bespoke submit button (custom POST loop); its
      // label + visibility are sourced from this action so the layout editor
      // surfaces an `action` target. EntityForm's built-in submit is never used
      // (see file header).
      submit: { visible: true, label: SUBMIT_LABEL },
      cancel: { visible: false },
      reset: { visible: false },
    },
    layout: { columns: 1 },
  };
}

/**
 * Poste-list column descriptors — stable keys are the contract; the French
 * headers are display-only / overridable. Declared as standalone constants
 * (never via `Record` indexing) so they double as the never-undefined fallback
 * the merged lookups resolve against.
 */
const POSTE_COLUMN_DEF: ColumnConfig = { label: 'Poste', order: 10, align: 'left' };
const MONTANT_COLUMN_DEF: ColumnConfig = { label: 'Montant', order: 20, align: 'right' };

const posteColumns: Record<string, ColumnConfig> = {
  [COL_POSTE]: POSTE_COLUMN_DEF,
  [COL_MONTANT]: MONTANT_COLUMN_DEF,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EstimationRecoursCard({
  ses,
  idSin,
  ncbtSin = '',
  deductionPostes = DEFAULT_DEDUCTION_POSTES,
  guaranteesToClose = [],
  onEstimationSaved,
  refreshKey,
  _layoutOverrides,
}: Readonly<EstimationRecoursCardProps>) {
  const { toast } = useToast();
  // The comment `user` (VEOS `CD_UTIL`), loaded at login (mahhp 22858).
  const cdUtil = useClaimFlowAuthStore((s) => s.cdUtil);

  // Collapsed on first paint — every card in the claim view's right column
  // opens folded.
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<EstimationSubmitError | null>(null);

  const [estimations, setEstimations] = useState<EstimationRecoursRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Per-row state, keyed by CD_GAR-CD_PSD, for every estimation row (all
  // garanties). We slice the visible subset by selectedGar at render time.
  const [posteRows, setPosteRows] = useState<Record<string, PosteRow>>({});

  // Stable lookup of deduction codes.
  const deductionSet = useMemo(() => new Set(deductionPostes), [deductionPostes]);

  // -------------------------------------------------------------------------
  // The single load GET, guarded by a `cancelled` flag and re-run whenever
  // `idSin`/`refreshKey` change (the parent bumps `refreshKey` to force a
  // reload after a dependent mutation, e.g. OpenCoverageCard opening
  // guarantees). StrictMode-safe: the second mount simply re-fetches, and any
  // superseded run is ignored via `cancelled` (mirror EstimationCard).
  //
  // Unlike the provision card there is NO second `sinistre-inline-get` call:
  // with no perte-totale checkbox there is no total-loss flag to read.
  // -------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    async function fetchEstimations() {
      try {
        const response = await claimflowApiClient.get<ErpListResponse<EstimationRecoursRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-tab-estimations-recours-all',
          // perPage:1000 is MANDATORY — the default page size truncates the list.
          { params: { id_sin: idSin, perPage: 1000 } }
        );
        if (!cancelled && response.data.success) {
          const data = response.data.data.data;
          setEstimations(data);

          // Build per-row form state for every poste.
          const next: Record<string, PosteRow> = {};
          for (const row of data) {
            const baseline = prefillValue(row);
            next[rowKey(row)] = { row, value: baseline, baseline, error: undefined };
          }
          setPosteRows(next);
        }
      } catch {
        // silently fail — poste list stays empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchEstimations();

    return () => {
      cancelled = true;
    };
  }, [idSin, refreshKey]);

  // -------------------------------------------------------------------------
  // Derived: selector options + visible rows (filtered to selectedGar)
  // -------------------------------------------------------------------------

  // Garantie options come from the `guaranteesToClose` prop (full replace —
  // the SOLE source of the select options). Dedupe by CD_GAR in
  // first-appearance order, keeping the first LIB_GAR seen for that code. The
  // poste rows are still sourced from the estimations fetch (filtered by the
  // selected CD_GAR), not from this prop. Typed `SelectableOption[]` so it
  // feeds the EntityForm select config.
  const garantieItems = useMemo<SelectableOption[]>(() => {
    const seen = new Set<string>();
    const items: SelectableOption[] = [];
    for (const gar of guaranteesToClose) {
      if (seen.has(gar.CD_GAR)) continue;
      seen.add(gar.CD_GAR);
      items.push({ value: gar.CD_GAR, label: gar.LIB_GAR });
    }
    return items;
  }, [guaranteesToClose]);

  // -------------------------------------------------------------------------
  // EntityForm (controlled): the `garantie` form control (top group) and the
  // `commentaire` textarea (bottom group). Their VALUES live in the form
  // store; everything numeric/computed stays in this card's own state. Layout
  // overrides merge into the field config.
  // -------------------------------------------------------------------------

  const baseFormConfig = useMemo(
    () => buildEstimationRecoursFormConfig(garantieItems),
    [garantieItems]
  );

  usePublishLayoutConfig('claimflow.estimationRecoursCard', {
    ...buildPublishedConfigFromEntityForm(baseFormConfig, CARD_TITLE),
    columns: { ...posteColumns },
  });

  const formConfig = useMemo(
    () => applyLayoutOverrides(baseFormConfig, _layoutOverrides),
    [baseFormConfig, _layoutOverrides]
  );

  const form = useEntityForm({ config: formConfig });

  // Read the field value back from the form store (controlled mode).
  const selectedGarValue = form.getValue(FIELD_GARANTIE);
  const selectedGar = typeof selectedGarValue === 'string' ? selectedGarValue : '';

  // Two field groups over the SAME config + form store: the top group renders
  // every field but the comment group; the comment group renders under the
  // « Recours estimé » total (mockup). Both stay `<EntityForm.Fields/>`
  // auto-renders, so the visibility / order / label overrides apply in each.
  const mainGroupFields = useMemo(
    () => Object.keys(formConfig.fields).filter((key) => !COMMENT_GROUP_FIELDS.includes(key)),
    [formConfig.fields]
  );
  const commentGroupVisible = COMMENT_GROUP_FIELDS.some((key) => form.isFieldVisible(key));

  // Resolved poste column descriptors (effective label / hidden / order /
  // align / tooltip after the merge). Empty overrides ⇒ identity.
  const resolvedPosteColumns = useMemo(
    () => applyColumnOverrides(posteColumns, _layoutOverrides?.columns),
    [_layoutOverrides]
  );

  // Default the selected garantie to the first derived option once estimations
  // load. Guarded by `selectedGar === ''` so we never clobber a user's pick:
  // after the user selects another garantie, selectedGar is non-empty and this
  // effect is a no-op.
  useEffect(() => {
    const [first] = garantieItems;
    if (selectedGar === '' && first) {
      form.setValue(FIELD_GARANTIE, first.value);
    }
  }, [selectedGar, garantieItems, form]);

  const visibleRows = useMemo(
    () =>
      estimations
        .filter((r) => r.CD_GAR === selectedGar)
        .map((r) => posteRows[rowKey(r)])
        .filter((pr): pr is PosteRow => pr !== undefined),
    [estimations, selectedGar, posteRows]
  );

  // -------------------------------------------------------------------------
  // Recours estimé: Σ(non-deduction) − Σ(|deduction|). Recomputed
  // synchronously. An empty / non-numeric input contributes 0
  // (`parseFloat('') || 0`). Deduction postes are subtracted on their
  // MAGNITUDE, so the total is right whether the amount reads `-1000` (forced
  // sign) or a legacy `1000`.
  // -------------------------------------------------------------------------

  const recoursTotal = useMemo(
    () =>
      visibleRows.reduce((acc, pr) => {
        const amount = Number.parseFloat(pr.value) || 0;
        return deductionSet.has(pr.row.CD_PSD) ? acc - Math.abs(amount) : acc + amount;
      }, 0),
    [visibleRows, deductionSet]
  );

  // -------------------------------------------------------------------------
  // Row editing + validation (MINIMUM floor)
  //
  // A deduction poste's amount is forced NEGATIVE as the user types (mahhp
  // 22851, same as `EstimationCard`): a non-zero value becomes `-|n|` (typing
  // `1000` shows `-1000`, deleting the sign puts it back), while `''`, `0` and
  // partial input stay as typed. The same call runs on blur to normalise
  // whatever the field holds. Its MINIMUM floor is checked on the magnitude.
  // -------------------------------------------------------------------------

  const updateAmount = useCallback(
    (key: string, rawValue: string) => {
      setPosteRows((prev) => {
        const pr = prev[key];
        if (!pr) return prev;
        const isDeduction = deductionSet.has(pr.row.CD_PSD);
        const value = signedAmountString(rawValue, isDeduction);
        let error: string | undefined;
        const minimum = pr.row.MINIMUM;
        // Live rows may carry `MINIMUM: null` (no floor at all) — and
        // defensively also `undefined` / '' — in which case no validation
        // applies.
        if (hasValue(minimum)) {
          const min = Number(minimum);
          if (!Number.isNaN(min) && isBelowMinimum(value, min, isDeduction)) {
            error = `Minimum ${String(minimum)} € déjà réglé`;
          }
        }
        if (value === pr.value && error === pr.error) return prev;
        return { ...prev, [key]: { ...pr, value, error } };
      });
    },
    [deductionSet]
  );

  const hasVisibleError = useMemo(() => visibleRows.some((pr) => Boolean(pr.error)), [visibleRows]);

  // Submit scope: the MODIFIED visible rows only (spec 22442 — « on itère
  // seulement sur les montants modifiés »). This differs from `EstimationCard`,
  // which posts every filled row; it also means the gate below is "something
  // was edited" rather than "something is filled" — an untouched pre-filled
  // estimation has nothing to persist. Compared SIGNED (like the highlight), so
  // a legacy positive deduction merely displayed negative is not "modified".
  const modifiedRows = useMemo(
    () =>
      visibleRows.filter((pr) => {
        const isDeduction = deductionSet.has(pr.row.CD_PSD);
        return (
          signedAmountString(pr.value, isDeduction) !==
          signedAmountString(pr.baseline, isDeduction)
        );
      }),
    [visibleRows, deductionSet]
  );

  const submitDisabled = submitting || modifiedRows.length === 0 || hasVisibleError;

  // -------------------------------------------------------------------------
  // Submit — one POST per modified poste, then reset those rows' baselines
  // -------------------------------------------------------------------------

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);

    // (a) Commentaire gate (mahhp 22858) — checked before ANY call. Config
    // driven: a field an admin hid (`hideField`, or a `setFieldCondition`
    // hide) is neither required nor posted, but the pre-check still runs.
    const commentVisible = form.isFieldVisible(FIELD_COMMENTAIRE);
    const comment = commentVisible ? commentValue(form.getValue(FIELD_COMMENTAIRE)) : '';
    if (commentVisible && form.isFieldRequired(FIELD_COMMENTAIRE) && comment === '') {
      form.setError(FIELD_COMMENTAIRE, requiredMessageOf(formConfig.fields[FIELD_COMMENTAIRE]));
      return;
    }
    // The comment `user` is the session's CD_UTIL — without it nothing is sent.
    let commentCreator: string | null = null;
    if (comment !== '') {
      if (!cdUtil) {
        setSubmitError({ message: ESTIMATION_COMMENT_TEXT.missingCdUtil });
        return;
      }
      commentCreator = cdUtil;
    }
    if (commentVisible) form.clearError(FIELD_COMMENTAIRE);

    setSubmitting(true);

    // Snapshotted here so the POST loop and the baseline reset agree on the
    // exact set of rows.
    const rowsToPost = modifiedRows;

    // The amount each poste is written with — a deduction poste NEGATIVE (VEOS
    // only deducts it with the sign), guarded here too for a legacy positive
    // row. The pre-check forwards the very same value.
    const savedAmount = (pr: PosteRow) =>
      savedPosteAmount(pr.value, deductionSet.has(pr.row.CD_PSD));

    try {
      // (b) VEOS pre-check (WS2_GS_VER_ESTIMATION) — fail closed: anything but
      // `VERIF = OK` blocks the submit before any write. VEOS checks the whole
      // estimation, so it receives EVERY visible poste with an amount (not
      // only the modified rows written below), signed as it is saved.
      const control = await estimationControlService.verifyEstimation({
        idSin,
        estimation: 'recours',
        garantie: selectedGar,
        postes: Object.fromEntries(
          visibleRows
            .filter((pr) => hasValue(pr.value))
            .map((pr) => [pr.row.CD_PSD, savedAmount(pr)])
        ),
        total: recoursTotal,
        commentaire: comment,
      });
      if (!control.ok) {
        // Titled « Contrôle VEOS » by the service: the message is VEOS', not
        // an echo of the user's comment.
        setSubmitError({ title: control.title, message: control.message });
        return;
      }

      // (c) The prevision writes — unchanged.
      try {
        for (const pr of rowsToPost) {
          const postResponse = await claimflowApiClient.post<ErpProxyResponse<unknown>>(
            // The dedicated `sinistre-prevision` endpoint
            // (`POST /sinistre/{id_sin}/prevision`) persists each modified
            // recours estimation (spec mahhp 22442).
            '/claimflow/erp/registries/veos/endpoints/sinistre-prevision',
            {
              data: {
                cdGar: pr.row.CD_GAR,
                mnt: savedAmount(pr),
                postDom: pr.row.CD_PSD,
                id_sin: idSin,
              },
            }
          );
          if (!postResponse.data.success) {
            throw new Error(
              postResponse.data.error?.message ?? 'Échec de l’estimation de recours'
            );
          }
        }

        // Reset each posted row's baseline to its saved value so the
        // modified-row Pencil highlight clears for everything we just persisted.
        setPosteRows((prev) => {
          const next = { ...prev };
          for (const pr of rowsToPost) {
            const key = rowKey(pr.row);
            next[key] = { ...pr, baseline: pr.value };
          }
          return next;
        });
      } catch (err) {
        const message = extractErpErrorMessage(err);
        setSubmitError({
          message:
            message ??
            (err instanceof Error ? err.message : 'Échec de l’enregistrement de l’estimation.'),
        });
        return;
      }

      // (d) Post the comment on the claim. The estimation is already saved,
      // so a failure here still refreshes the parent and keeps the text in the
      // field (to be copied into the Commentaires panel) — never silent.
      if (commentCreator !== null) {
        try {
          await commentsService.postCommentWithMentions({
            ses,
            idSin,
            ncbtSin,
            comment,
            creator: commentCreator,
            mentions: [],
          });
        } catch (err) {
          onEstimationSaved?.();
          setSubmitError({
            message: ESTIMATION_COMMENT_TEXT.commentNotSent(
              'L’estimation de recours a été enregistrée',
              requestErrorDetail(err)
            ),
          });
          return;
        }
      }

      // (e) Success as before, plus a fresh (empty) comment for the next one.
      toast({ variant: 'success', title: 'Estimation de recours enregistrée' });
      onEstimationSaved?.();
      setExpanded(false);
      if (commentVisible) form.setValue(FIELD_COMMENTAIRE, '');
    } finally {
      setSubmitting(false);
    }
  }, [
    form,
    formConfig.fields,
    cdUtil,
    ses,
    idSin,
    ncbtSin,
    selectedGar,
    visibleRows,
    recoursTotal,
    modifiedRows,
    deductionSet,
    toast,
    onEstimationSaved,
  ]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // The merge never drops keys, so these always resolve; the `?? def` keeps
  // the indexed-access type non-undefined without changing runtime behaviour.
  const posteCol = resolvedPosteColumns[COL_POSTE] ?? POSTE_COLUMN_DEF;
  const montantCol = resolvedPosteColumns[COL_MONTANT] ?? MONTANT_COLUMN_DEF;

  return (
    <Card id={actionAnchorId('claimflow.estimationRecoursCard')}>
      <Card.Header collapsible defaultExpanded={expanded} onExpandChange={setExpanded}>
        <Icon icon={Calculator} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle(CARD_TITLE, _layoutOverrides)}
        </Text>
      </Card.Header>
      <Card.Content>
        <EntityForm config={formConfig} form={form}>
          <EntityForm.Fields className="mb-4" exclude={COMMENT_GROUP_FIELDS} />
        </EntityForm>

        {/* Poste list — bordered container, one row per poste, no header row. */}
        <Panel variant="outlined" padding="default">
          {loading ? (
            <div className="flex items-center justify-center min-h-32">
              <Spinner size="md" />
            </div>
          ) : visibleRows.length === 0 ? (
            <Text size="sm" color="muted">
              Aucun poste pour cette garantie
            </Text>
          ) : (
            visibleRows.map((pr, idx) => {
              const isDeduction = deductionSet.has(pr.row.CD_PSD);
              const label = isDeduction ? `${pr.row.LIB_PSD} (−)` : pr.row.LIB_PSD;
              // Deduction postes always display negative — including a legacy
              // positive stored amount (mahhp 22851).
              const displayValue = signedAmountString(pr.value, isDeduction);
              // A poste is "modified" when its value differs from its baseline
              // (both signed — same test as `modifiedRows`). Same treatment as
              // `EstimationCard` (spec: « comme sur le composant nouvelle
              // provision ») — primary-tinted wrapper + a Pencil indicator on
              // the label, NOT the mockup's amber.
              const isModified =
                displayValue !== signedAmountString(pr.baseline, isDeduction);
              const wrapperClass = isModified
                ? 'rounded-md border border-primary-200 bg-primary-50/40 px-2 py-1'
                : 'px-2 py-1';
              return (
                <div key={rowKey(pr.row)}>
                  {idx > 0 && <Divider spacing={2} />}
                  <div className={wrapperClass}>
                    <HStack justify="between" align="center">
                      {posteCol.hidden ? null : (
                        <HStack gap={2} align="center">
                          {isModified && (
                            <span data-testid={`poste-modified-${pr.row.CD_PSD}`}>
                              <Icon icon={Pencil} size="sm" color="primary" />
                            </span>
                          )}
                          <PosteLabel column={posteCol} text={label} isModified={isModified} />
                        </HStack>
                      )}
                      {montantCol.hidden ? null : (
                        <div className="w-40">
                          <TextField
                            type="number"
                            size="sm"
                            value={displayValue}
                            onChange={(e) => {
                              updateAmount(rowKey(pr.row), e.target.value);
                            }}
                            // Deduction postes re-normalise on blur (force the
                            // sign on whatever the field holds); other postes
                            // keep their change-only behaviour.
                            onBlur={
                              isDeduction
                                ? (e) => {
                                    updateAmount(rowKey(pr.row), e.target.value);
                                  }
                                : undefined
                            }
                            // "0.00" is a numeric input FORMAT mask (an example
                            // of the expected decimal shape), not a
                            // tenant-facing label — it has no config slot and is
                            // intentionally left literal (same documented
                            // exemption as `EstimationCard`).
                            placeholder="0.00"
                            className="text-right"
                            error={Boolean(pr.error)}
                            errorMessage={pr.error}
                            aria-label={`${montantCol.label} pour ${pr.row.LIB_PSD}`}
                          />
                        </div>
                      )}
                    </HStack>
                  </div>
                </div>
              );
            })
          )}
        </Panel>

        {/* Recours estimé — subtle grey row beneath the list. */}
        <div className="mt-3 flex items-center justify-between rounded-md bg-gray-50 px-4 py-3">
          <Text size="sm" color="muted">
            Recours estimé
          </Text>
          <Text size="base" weight="bold" color="primary">
            {EUR_FORMATTER.format(recoursTotal)}
          </Text>
        </div>

        {/* Comment group (mahhp 22858) — the config-driven Commentaire field,
            under the « Recours estimé » total and above the submit button. A
            second EntityForm root over the SAME config + form store (not a
            separate form), so values, errors and layout overrides are shared. */}
        {commentGroupVisible && (
          <EntityForm config={formConfig} form={form} className="mt-4">
            <EntityForm.Fields exclude={mainGroupFields} />
          </EntityForm>
        )}

        {/* Submit error — right above the submit button (not at the top of
            the card, out of sight after a click). A blocked VEOS pre-check
            carries the title « Contrôle VEOS »; the other errors none. */}
        {submitError && (
          <Alert variant="error" title={submitError.title} className="mt-4">
            {submitError.message}
          </Alert>
        )}

        {formConfig.actions?.submit?.visible !== false && (
          <div className="pt-4">
            <Button
              type="button"
              variant="primary"
              leftIcon={<Icon icon={Save} size="sm" />}
              loading={submitting}
              disabled={submitDisabled}
              onClick={handleSubmit}
              className="w-full"
            >
              {formConfig.actions?.submit?.label ?? SUBMIT_LABEL}
            </Button>
          </div>
        )}
      </Card.Content>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Imperative column-header renderer (reads the resolved descriptor)
// ---------------------------------------------------------------------------

/**
 * Renders a poste row's label cell. The header text is the resolved column's
 * effective `label`-driven cell content; the deduction " (−)" marker is
 * data-derived and already folded into `text`. A `headerTooltip` (if set by a
 * rule) reveals on hover via the shared {@link Tooltip}.
 */
function PosteLabel({
  column,
  text,
  isModified = false,
}: Readonly<{ column: ColumnConfig; text: string; isModified?: boolean }>) {
  const label = (
    <Text size="sm" color={isModified ? 'primary' : 'default'}>
      {text}
    </Text>
  );
  if (column.headerTooltip) {
    return <Tooltip content={column.headerTooltip}>{label}</Tooltip>;
  }
  return label;
}
